import { Typography, TypographyVariants } from "@atoms/typography";
import styled from "styled-components";
import React, { useState } from "react";
import { useFHConfig } from "@templates/config";
import Image from "next/image";
import Link from "next/link";

export interface ReviewerCardData {
  id: number;
  title: string;
  image: string;
  description: string;
  review_text: string;
  reviewer_name: string;
  propertySlug: string;
}
interface CardListProps {
  cards: ReviewerCardData[];
}

export const CardList = ({ cards }: CardListProps) => {
  const config = useFHConfig();
  return (
    <ScrollContainer>
      {cards.map((card) => (
        <Card key={card.id}>
          <CardImageWrapper>
            <Link href={`/homes/${card.propertySlug}`} target="_blank" rel="noopener noreferrer">
              <Image
                src={`${config.backUrl}/assets/${card.image}`}
                alt={card.title}
                fill
                sizes="(max-width: 768px) 100vw, 350px"
                style={{ objectFit: "cover" }}
                priority
              />
            </Link>
          </CardImageWrapper>
          <CardText>
            <Typography variant="title3">{card.title}</Typography>
            <ClampedText>
              <ExpandableText text={card.description} maxLines={3} variantType={"body2"} />
            </ClampedText>
          </CardText>

          <Separator />

          <ReviewSection>
            <Stars>★★★★★</Stars>
            <ClampedText>
              <ExpandableText text={`"${card.review_text}"`} maxLines={2} variantType={"body3"} />
            </ClampedText>
            <Reviewer variant="body3">- {card.reviewer_name}</Reviewer>
          </ReviewSection>
        </Card>
      ))}
    </ScrollContainer>
  );
};

// Expandable text component
const ExpandableText = ({
  text,
  maxLines,
  variantType,
}: {
  text: string;
  maxLines: number;
  variantType: TypographyVariants;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TextContent expanded={expanded} maxLines={maxLines}>
        <Typography variant={variantType}>{text}</Typography>
      </TextContent>
      {text.length > 120 && (
        <ToggleLink onClick={() => setExpanded(!expanded)}>{expanded ? "See less" : "See more"}</ToggleLink>
      )}
    </>
  );
};

const ScrollContainer = styled.div`
  display: flex;
  justify-content: space-between;
  overflow-x: auto;
  gap: 16px;
  padding-bottom: 16px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Card = styled.div`
  width: 350px;
  flex-shrink: 0;
  scroll-snap-align: start;
  border: 1px solid #ccc;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 170px;
  overflow: hidden;
`;

const CardText = styled.div`
  padding: 12px;
`;

const Separator = styled.hr`
  margin: 0 12px;
  border: none;
  border-top: 1px solid #eee;
`;

const ReviewSection = styled.div`
  padding: 12px;
  font-size: 14px;
  color: #444;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Stars = styled.div`
  color: #00a86b;
  font-size: 16px;
`;

const Reviewer = styled(Typography)`
  text-align: right;
  font-size: 14px;
  color: #777;
`;

const ClampedText = styled.div`
  margin-bottom: 8px;
`;

const TextContent = styled.div<{ expanded: boolean; maxLines: number }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ expanded, maxLines }) => (expanded ? "none" : maxLines)};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ToggleLink = styled.span`
  color: #0070f3;
  cursor: pointer;
  font-size: 14px;
  text-decoration: underline;
`;
